'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Eye, Target, Clock, MessageCircle, Lightbulb, BookOpen, ArrowRight } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function TekKartPage() {
  const sampleQuestions = [
    "Bugün hangi konuya odaklanmalıyım?",
    "Bu durumda ne yapmam gerekiyor?",
    "Şu anki enerjim nasıl?",
    "Bu konuda bana ne tavsiye ediliyor?",
    "Hangi yaklaşımı benimsemeliyim?",
    "Bu durumun temel mesajı nedir?"
  ];

  const interpretationSteps = [
    {
      step: 1,
      title: "İlk Bakış",
      description: "Kartı çektiğinizde ilk gözünüze çarpan detayı not edin",
      icon: <Eye className="h-5 w-5" />
    },
    {
      step: 2,
      title: "Genel Enerji",
      description: "Kartın genel havasını ve size hissettirdiklerini değerlendirin",
      icon: <Target className="h-5 w-5" />
    },
    {
      step: 3,
      title: "Sembol Analizi",
      description: "Kartdaki renk, sayı ve figürlerin anlamlarını inceleyin",
      icon: <Star className="h-5 w-5" />
    },
    {
      step: 4,
      title: "Kişisel Bağlantı",
      description: "Kartı yaşamınızdaki mevcut durumla ilişkilendirin",
      icon: <MessageCircle className="h-5 w-5" />
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Tek Kart Çekimi</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center">
              <Star className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Tek Kart Çekimi</h1>
              <p className="text-green-300 font-medium">Başlangıç Seviyesi • 1 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            En basit ve etkili tarot okuma yöntemi. Günlük rehberlik, hızlı içgörü veya net cevap almak için ideal.
            Tarot yolculuğunuzun başlangıç noktası.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">2-5 dakika</p>
            <p className="text-slate-400 text-sm">Hızlı ve pratik</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Günlük rehberlik</p>
            <p className="text-slate-400 text-sm">Odak noktası belirleme</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-green-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">Başlangıç</p>
            <p className="text-slate-400 text-sm">Herkes için uygun</p>
          </div>
        </div>

        {/* Nasıl Yapılır */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Nasıl Yapılır?</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Hazırlık</h3>
                  <p className="text-slate-300 mb-2">
                    Kartlarınızı karıştırın ve sorunuzu net bir şekilde zihninizte formüle edin.
                    Sakin bir ortamda, odaklanmış bir şekilde başlayın.
                  </p>
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-slate-400 text-sm italic">
                      İpucu: Sorunuzu yüksek sesle söylemek odaklanmanıza yardımcı olabilir.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kart Seçimi</h3>
                  <p className="text-slate-300 mb-2">
                    Kartları yüz aşağı dizin veya desteyi elinizde tutun. Sezginize güvenerek
                    size doğru gelen kartı seçin.
                  </p>
                  <div className="bg-slate-700 rounded-lg p-3">
                    <p className="text-slate-400 text-sm italic">
                      İpucu: Eline daha sıcak gelen, daha çok dikkatinizi çeken kartı seçin.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartı Çevirin</h3>
                  <p className="text-slate-300 mb-2">
                    Seçtiğiniz kartı yavaşça çevirin ve önünüze yerleştirin.
                    İlk gördüğünüzde hissettiğiniz duyguyu not edin.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Yorumlama</h3>
                  <p className="text-slate-300">
                    Kartın anlamını sorunuzla ilişkilendirin. Geleneksel anlamları bilgiyle
                    birleştirin ama kendi sezginizi de dinleyin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Örnek Sorular */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sorulabilecek Sorular</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sampleQuestions.map((question, index) => (
              <div key={index} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-start gap-3">
                  <MessageCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                  <p className="text-slate-300 text-sm">{question}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yorumlama Adımları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama Adımları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationSteps.map((step) => (
              <div key={step.step} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                    {step.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100">{step.title}</h3>
                    <span className="text-green-300 text-sm">Adım {step.step}</span>
                  </div>
                </div>
                <p className="text-slate-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pratik İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik İpuçları</h2>
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-slate-100 font-medium">Günlük Rutin Haline Getirin</h4>
                  <p className="text-slate-400 text-sm">Her sabah bir kart çekerek gününüz için rehberlik alın.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-slate-100 font-medium">Notlar Alın</h4>
                  <p className="text-slate-400 text-sm">Çektiğiniz kartları ve yorumlarınızı not ederek gelişiminizi takip edin.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-slate-100 font-medium">Sabırlı Olun</h4>
                  <p className="text-slate-400 text-sm">Her kart çekiminde mükemmel yorumlar beklemeyin, pratik yapmaya devam edin.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h4 className="text-slate-100 font-medium">Çok Sık Çekmeyin</h4>
                  <p className="text-slate-400 text-sm">Aynı soru için art arda kart çekmek yerine, aldığınız cevapla oturun.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ne Zaman Kullanılır */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ne Zaman Kullanılır?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-900/20 border border-green-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-green-200 mb-4">İdeal Durumlar</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Günlük rehberlik almak</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Hızlı karar vermek</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Enerjinizi kontrol etmek</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                  <span className="text-green-100 text-sm">Tarot öğrenmeye başlamak</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-4">Uygun Olmayan Durumlar</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Karmaşık yaşam kararları</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Detaylı analiz gereken durumlar</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Birden fazla seçeneğin karşılaştırılması</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-amber-400 rounded-full"></div>
                  <span className="text-amber-100 text-sm">Zaman çizelgesi gereken sorular</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tüm Teknikler
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/uc-kart"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Sonraki: Üç Kart
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}